import React, { useRef, useState } from "react";
import axios from "axios";
import * as yup from "yup";
import useMeasure from "react-use-measure";
import { useFormik } from "formik";
import { animated } from "@react-spring/web";

import { API_URL } from "@app/constants";
import { useSelf } from "@app/utils/query";
import { useFeedbackState } from "@app/utils/emitters";
import { EVENTS, trackEvent } from "@app/utils/trackEvent";

import { Widget } from "./Components/Widget";
import { Done as DonePage } from "./Pages/Done";
import { OpenButton } from "./Components/OpenButton";
import { CloseButton } from "./Components/CloseButton";
import { EnterDetails as EnterDetailsPage } from "./Pages/EnterDetails";
import { ExtraDetails as ExtraDetailsPage } from "./Pages/ExtraDetails";
import { useFeedbackAnimation } from "./Hooks/useFeedbackAnimation";

import "./feedback.css";

const Feedback = () => {
  const timerRef = useRef<any>();
  const { data: self } = useSelf();
  const { isShowing, setIsShowing } = useFeedbackState();

  const [page, setPage] = useState(0);
  const [contentRef, { width, height }] = useMeasure();
  const [isFinished, setIsFinished] = useState(false);

  const {
    contentStyles,
    sectionStyles,
    buttonStyles,
    pageStyles,
    submitButtonStyles
  } = useFeedbackAnimation(isShowing, width, height, page, isFinished);

  const validationSchema = yup.object().shape({
    message: yup.string().min(5).required("Please enter 5 characters")
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      message: "",
      tags: [],
      email: self?.email || "",
      sendDetails: true,
      details: {
        userId: self?.id,
        page: window.location.href
      }
    },
    validationSchema: validationSchema,
    async onSubmit(values, { resetForm }) {
      console.log("onSubmit", values, { isValid: formik.isValid });
      await axios.post(`${API_URL}/contact`, values).then(() => {
        console.log("sent");
        resetForm();
      });
    }
  });

  // console.log(formik);

  const toggleFeedback = (e) => {
    trackEvent(EVENTS.FEEDBACK_TOGGLE);
    e.preventDefault();
    setIsShowing(!isShowing);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (isFinished) rewind();
  };

  const rewind = () => {
    setPage(0);
    formik.setFieldValue("message", "");
    formik.setFieldValue("email", self?.email || "");
    formik.setFieldValue("tags", []);
    setIsFinished(false);
  };

  const gotoNextPage = (e) => {
    e.preventDefault();
    setPage(page + 1);
  };

  const gotoPrevPage = (e) => {
    e.preventDefault();
    setPage(page - 1);
  };

  const gotoFinish = (e) => {
    e.preventDefault();
    setPage(2);
  };

  const finish = async (e) => {
    e.preventDefault();
    formik.handleSubmit();
    gotoFinish(e);
    setIsFinished(true);
    timerRef.current = setTimeout(toggleFeedback, 3000, e);
  };

  return (
    <Widget isShowing={isShowing} style={sectionStyles}>
      <OpenButton onClick={toggleFeedback} style={buttonStyles} />
      <animated.div className="feedback-contentWrap" style={contentStyles}>
        <div ref={contentRef} className="feedback-content">
          {/* Page 1 */}
          <EnterDetailsPage
            style={pageStyles}
            formik={formik}
            finish={finish}
            gotoNextPage={gotoNextPage}
            submitButtonStyles={submitButtonStyles}
          />
          {/* Page 2 */}
          <ExtraDetailsPage
            style={pageStyles}
            formik={formik}
            gotoPrevPage={gotoPrevPage}
            gotoNextPage={gotoNextPage}
            finish={finish}
            submitButtonStyles={submitButtonStyles}
          />
          {/* Page 3 */}
          <DonePage style={pageStyles} isVisible={page === 2} />
        </div>
        <CloseButton onClick={toggleFeedback} />
      </animated.div>
    </Widget>
  );
};

export { Feedback };
